import {useEffect, useReducer} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useResultStoryQuery, useResultStoryViewed} from '#apollo/schema';
import {LabResultMetricTypeEnum} from '#apollo/schema';
import {Text} from '#component/base';
import {FullscreenModalHeader, StoryControls, StorySteps, StoryVideos} from '#component/partial';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {ShareTrigger} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {initialState, reducer, STEPS, StoryHeaderVariant} from './ResultStory.util';

import * as css from './ResultStory.css';

const lastStep = STEPS.length - 1;
const markAsSeenIndex = STEPS.findIndex(({markAsSeen}) => !!markAsSeen);
const markAsSeenStep = markAsSeenIndex === -1 ? lastStep : markAsSeenIndex;

const getLogoColor = (variant: StoryHeaderVariant = 'primary') => {
  switch (variant) {
    case 'dark':
      return 'eggplant';
    case 'light':
      return 'cotton';
    case 'primary':
    default:
      return 'burgundy';
  }
};

const getButtonVariant = (variant: StoryHeaderVariant = 'primary') => {
  switch (variant) {
    case 'dark':
    case 'light':
      return 'secondary-dark';
    case 'primary':
    default:
      return 'secondary';
  }
};

export const ResultStoryScreen = () => {
  const [{activeStep, activeVideo, loadPercentage, controlsDisabled, useAltVideo}, dispatch] =
    useReducer(reducer, initialState);
  const {track} = useAnalytics();
  const {replace} = useRouter();
  const {isDesktop} = useViewportSize();
  const {data, loading} = useResultStoryQuery({
    variables: {
      metricTypes: [
        LabResultMetricTypeEnum.TallyGeneralAgePercentile,
        LabResultMetricTypeEnum.TallyCohortAgePercentile,
      ],
    },
    onCompleted: ({me}) => {
      const {mostRecentLabSubmissionWithResults} = me || {};
      const {deltaAge} = mostRecentLabSubmissionWithResults?.labAssessment || {};

      if (!mostRecentLabSubmissionWithResults) {
        replace('/dashboard');
      } else if (typeof deltaAge === 'number' && deltaAge > 0) {
        dispatch({type: 'SetAltVideos', payload: deltaAge > 0});
      }
    },
  });
  const {mostRecentLabSubmissionWithResults, subscription, userProfile} = data?.me || {};
  const {hasActiveSubscription} = subscription || {};
  const {firstName} = userProfile || {};
  const {labAssessment, labRecord, id, surveySubmissionId} =
    mostRecentLabSubmissionWithResults || {};
  const deltaAge = labAssessment?.deltaAge || 0;
  const [resultStoryViewed] = useResultStoryViewed({variables: {input: {labSubmissionId: id}}});
  const activeStepData = STEPS[activeStep];
  const activeStepName = activeStepData.name;
  const hasViewedResultStory = !!labRecord && labRecord.hasViewedResultStory;
  const isYounger = (labAssessment?.deltaAge || 0) < 0;
  const videosLoaded = !loading && !!labAssessment && loadPercentage === 100;
  const nextDisabled = activeStep >= lastStep || activeStepData.autoAdvance || controlsDisabled;
  const previousDisabled = activeStep <= 1 || activeStepData.autoAdvance || controlsDisabled;
  const sharePercentile =
    labAssessment?.labResultMetrics && labAssessment?.labResultMetrics.length > 0
      ? labAssessment.labResultMetrics[0].percentile
      : 0;

  const handleIncrementStep = () => {
    dispatch({type: 'IncrementStep'});
  };

  const handleDecrementStep = () => {
    dispatch({type: 'DecrementStep'});
  };

  const handleVideoLoaded = ({percent = 0}) => {
    dispatch({type: 'SetLoadPercentage', payload: percent});
  };

  const handleVideoEnded = () => {
    dispatch({type: 'IncrementVideo', payload: {isDesktop}});
  };

  useEffect(() => {
    if (activeStep === markAsSeenStep && !hasViewedResultStory) {
      resultStoryViewed();
    }
  }, [activeStep, hasViewedResultStory, resultStoryViewed]);

  useEffect(() => {
    if (activeStepName) {
      const trackedActiveStepName =
        activeStep === lastStep && hasActiveSubscription ? 'Results Story Survey' : activeStepName;
      track<PageViewEvent>({evtName: EventType.PageView, pageName: trackedActiveStepName});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, activeStepName, hasActiveSubscription]);

  useEffect(() => {
    const timeout = controlsDisabled
      ? setTimeout(() => {
          if (controlsDisabled) {
            dispatch({type: 'EnableControls'});
          }
        }, 3000)
      : undefined;

    return () => {
      clearTimeout(timeout);
    };
  }, [controlsDisabled]);

  return (
    <div className={css.root}>
      <div className={css.storyContainer}>
        <div className={sprinkles({pos: 'absolute', inset: 0})}>
          <StoryVideos
            autoAdvance={activeStepData.autoAdvance}
            activeVideo={activeVideo}
            activeStep={activeStep}
            className={sprinkles({
              inset: 0,
              opacity: videosLoaded ? 1 : 0,
              position: 'absolute',
              z: 0,
            })}
            onVideoEnded={handleVideoEnded}
            onLoaded={handleVideoLoaded}
            playbackRates={activeStepData.playbackRates}
            useAltVideo={useAltVideo}
          />
        </div>

        <div className={sprinkles({pos: 'absolute', inset: 0})}>
          <FullscreenModalHeader
            className={css.header}
            hideCloseButton={!hasViewedResultStory}
            logoColor={getLogoColor(activeStepData.headerVariant)}
            buttonVariant={getButtonVariant(activeStepData.headerVariant)}
          />

          {videosLoaded ? (
            // StoryVideos loads and manages playback of a step's videos, while
            // StorySteps renders a step's UI (ie, text, buttons) on top of them.
            <StorySteps
              afterEnter={() => {
                dispatch({type: 'EnableControls'});
              }}
              activeStep={activeStep}
              deltaAge={deltaAge}
              firstName={firstName}
              formattedBioAge={labAssessment?.formattedBioAge}
              formattedChronologicalAge={labAssessment?.formattedChronologicalAge}
              formattedDeltaAge={labAssessment?.formattedDeltaAge}
              hasActiveSubscription={hasActiveSubscription}
              isYounger={isYounger}
              labResultReportUrl={labAssessment?.reportUrl}
              onNext={handleIncrementStep}
              percentiles={labAssessment?.labResultMetrics}
              surveySubmissionId={surveySubmissionId}
            />
          ) : (
            <div className={css.loadingContainer}>
              <Text color="white">Loading</Text>

              <div className={css.progressBar}>
                <div
                  className={css.progressBarInner}
                  style={{transform: `scaleX(${loadPercentage}%)`}}
                />
              </div>
            </div>
          )}
        </div>

        <ShareTrigger
          formattedDeltaAge={labAssessment?.formattedDeltaAge}
          isYounger={isYounger}
          percentile={sharePercentile}>
          {({onClick}) => (
            <StoryControls
              className={css.controls}
              nextDisabled={nextDisabled}
              onNext={handleIncrementStep}
              onPrevious={handleDecrementStep}
              onShare={onClick}
              previousDisabled={previousDisabled}
              shareVisible={activeStepData.showShare}
              show={activeStepData.showControls}
              variant={activeStepData.controlsVariant}
            />
          )}
        </ShareTrigger>
      </div>
    </div>
  );
};

export const ResultStory: NextPage = withRequireUserState(ResultStoryScreen, {
  userState: 'verified',
});
